import React, { FC, useCallback, useContext } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  GetInventoryDocument,
  GetUserSelfTrackedLearningDocument,
  UpdateUserSelfTrackedLearningDocument,
  RemoveUserSelfTrackedLearningDocument,
} from '@/store/v2';
import { UserSelfTrackedLearning } from '@/component/UserSelfTrackedLearning/types';
import { UserSelfTrackedLearningForm } from '@/component/UserSelfTrackedLearning/components/UserSelfTrackedLearningForm/UserSelfTrackedLearningForm';
import { SharedSnackbarContext, SnackbarType } from '@/component/SharedSnackbar/SharedSnackbar';

type EditUserSelfTrackedLearningProps = {
  userSelfTrackedLearningId?: number;
  isEditing?: boolean;
  onClose?: () => void;
};

export const UpdateUserSelfTrackedLearning: FC<EditUserSelfTrackedLearningProps> = ({
  userSelfTrackedLearningId,
  isEditing,
  onClose,
}) => {
  const { t } = useTranslation('userSelfTrackedLearning');
  const { openSnackbar } = useContext(SharedSnackbarContext);

  const [updateUserSelfTrackedLearning, { loading }] = useMutation<{
    updateSelfTrackedLearning: { success: boolean };
  }>(UpdateUserSelfTrackedLearningDocument, {
    refetchQueries: [{ query: GetInventoryDocument }],
  });

  const [removeUserSelfTrackedLearning] = useMutation<{ removeSelfTrackedLearning: { success: boolean } }>(
    RemoveUserSelfTrackedLearningDocument,
    {
      refetchQueries: [{ query: GetInventoryDocument }],
    },
  );

  const { data } = useQuery<{ selfTrackedLearning: UserSelfTrackedLearning }>(GetUserSelfTrackedLearningDocument, {
    fetchPolicy: 'no-cache',
    variables: {
      userSelfTrackedLearningId,
    },
  });

  const learning: UserSelfTrackedLearning | undefined = data?.selfTrackedLearning;

  const handleUpdateSelfTrackedLearning = useCallback(async (userSelfTrackedLearning: UserSelfTrackedLearning) => {
    const { data } = await updateUserSelfTrackedLearning({ variables: { userSelfTrackedLearning } });

    if (data?.updateSelfTrackedLearning?.success) {
      openSnackbar({
        type: SnackbarType.SUCCESS,
        message: t('Learning has been updated'),
        isDismissive: true,
      });
    }

    if (onClose) {
      onClose();
    }
  }, []);

  const handleRemoveSelfTrackedLearning = useCallback(async (userSelfTrackedLearningId: number) => {
    const { data } = await removeUserSelfTrackedLearning({ variables: { userSelfTrackedLearningId } });

    if (data?.removeSelfTrackedLearning?.success) {
      openSnackbar({
        type: SnackbarType.SUCCESS,
        message: t('Learning has been removed'),
        isDismissive: true,
      });
    }

    if (onClose) {
      onClose();
    }
  }, []);

  if (!learning) {
    return null;
  }

  return (
    <UserSelfTrackedLearningForm
      save={handleUpdateSelfTrackedLearning}
      handleDelete={handleRemoveSelfTrackedLearning}
      userSelfTrackedLearning={learning}
      isEditing={isEditing}
      loading={loading}
    />
  );
};
